<template>
  <div class="cc-scene" @pointermove="onPointerMove" @pointerleave="onLeave">
    <div class="cc-float" :class="{ 'cc-float--busy': busy }">
      <div class="cc-tilt" ref="tilt">
        <div
          class="cc-card"
          :class="{ flipped: isFlipped, 'cc-card--busy': busy }"
          @click="flipCard"
          @keydown.enter.prevent="flipCard"
          @keydown.space.prevent="flipCard"
          role="button"
          tabindex="0"
          :aria-pressed="isFlipped ? 'true' : 'false'"
          :aria-label="$t('card.aria')"
        >
          <!-- Frente -->
          <div class="cc-face cc-front">
            <div class="cc-shine"></div>
            <div class="cc-glare"></div>
            <div v-if="burstKey" :key="burstKey" class="cc-burst"></div>
            <div class="cc-front__top">
              <div class="cc-chip"><span></span><span></span><span></span></div>
              <div class="cc-brand">
                <v-icon v-if="data.current" color="white" small class="mr-1"
                  >mdi-check-decagram</v-icon
                >
                <span class="cc-brand__text">pitch</span>
              </div>
            </div>

            <div
              class="cc-number cc-slot"
              :class="{ 'cc-slot--active': activeField === 'number' }"
              :aria-label="displayNumber"
            >
              <span
                v-for="(item, i) in numberChars"
                :key="'n' + i + item.char"
                class="cc-char"
                :class="{ 'cc-char--typed': item.typed }"
                aria-hidden="true"
                >{{ item.char }}</span
              >
            </div>

            <div class="cc-front__bottom">
              <div class="cc-field">
                <span class="cc-label">{{ $t("card.holder") }}</span>
                <span
                  class="cc-value cc-holder cc-slot"
                  :class="{ 'cc-slot--active': activeField === 'holder' }"
                  :aria-label="displayHolder"
                >
                  <span
                    v-for="(item, i) in holderChars"
                    :key="'h' + i + item.char"
                    class="cc-char"
                    :class="{ 'cc-char--typed': item.typed }"
                    aria-hidden="true"
                    >{{ item.char }}</span
                  >
                </span>
              </div>
              <div class="cc-field cc-field--right">
                <span class="cc-label">{{ $t("card.expiry") }}</span>
                <span
                  :key="'e' + displayExpiry"
                  class="cc-value cc-slot cc-char"
                  :class="{
                    'cc-slot--active': activeField === 'expiry',
                    'cc-char--typed': expiryTyped,
                  }"
                  >{{ displayExpiry }}</span
                >
              </div>
            </div>
          </div>

          <!-- Verso -->
          <div class="cc-face cc-back">
            <div class="cc-glare"></div>
            <div class="cc-stripe"></div>
            <div class="cc-signature">
              <span class="cc-signature__label">CVV</span>
              <span
                :key="'c' + displayCvv"
                class="cc-cvv cc-char"
                :class="{ 'cc-char--typed': !!data.cvv }"
                >{{ displayCvv }}</span
              >
            </div>
            <p class="cc-back__note">
              {{ $t("card.backNote") }}
            </p>
            <div class="cc-brand cc-brand--back">
              <span class="cc-brand__text">pitch english</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CardTemplate",
  props: {
    checkout: {
      type: Boolean,
      default: false,
    },
    isReadonly: {
      type: Boolean,
      default: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    // Campo do formulario em edicao: "number" | "holder" | "expiry" | "".
    activeField: {
      type: String,
      default: "",
    },
    // Tokenizando/salvando: o cartao pulsa enquanto espera.
    busy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFlipped: false,
      burstKey: 0,
      raf: null,
    };
  },
  computed: {
    displayNumber() {
      const raw = String(this.data.maskedNumber || "").trim();
      if (!raw) return "•••• •••• •••• ••••";
      // Completa visualmente ate 16 digitos com placeholders.
      const digits = raw.replace(/\s+/g, "");
      const padded = (digits + "••••••••••••••••").slice(0, 16);
      return padded.replace(/(.{4})/g, "$1 ").trim();
    },
    numberChars() {
      return Array.from(this.displayNumber).map((char) => ({
        char,
        typed: /\d/.test(char),
      }));
    },
    displayHolder() {
      return (
        String(this.data.holder || "")
          .trim()
          .toUpperCase() || this.$t("card.yourName")
      );
    },
    holderChars() {
      const typed = !!String(this.data.holder || "").trim();
      return Array.from(this.displayHolder).map((char) => ({ char, typed }));
    },
    displayExpiry() {
      const month = this.data.expMonth || "••";
      const year = String(this.data.expYear || "••••").slice(-2);
      return `${month}/${year}`;
    },
    expiryTyped() {
      return !!(this.data.expMonth || this.data.expYear);
    },
    displayCvv() {
      const cvv = String(this.data.cvv || "").trim();
      return cvv || "•••";
    },
    numberComplete() {
      return (
        String(this.data.maskedNumber || "").replace(/\D/g, "").length >= 16
      );
    },
  },
  watch: {
    // Numero completo: um brilho unico atravessa o cartao.
    numberComplete(done) {
      if (done) this.burstKey += 1;
    },
  },
  beforeDestroy() {
    if (this.raf) cancelAnimationFrame(this.raf);
  },
  methods: {
    flipCard() {
      this.isFlipped = !this.isFlipped;
    },
    setFlipped(value) {
      this.isFlipped = !!value;
    },
    // Inclinacao 3D e reflexo seguem o ponteiro. Mouse e toque passam por aqui.
    onPointerMove(event) {
      if (event.pointerType === "touch") return;
      if (this.raf) return;
      const { clientX, clientY } = event;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        const el = this.$refs.tilt;
        if (!el) return;
        const box = el.getBoundingClientRect();
        const px = Math.min(Math.max((clientX - box.left) / box.width, 0), 1);
        const py = Math.min(Math.max((clientY - box.top) / box.height, 0), 1);
        el.style.setProperty("--ry", `${(px - 0.5) * 16}deg`);
        el.style.setProperty("--rx", `${(0.5 - py) * 12}deg`);
        el.style.setProperty("--gx", `${px * 100}%`);
        el.style.setProperty("--gy", `${py * 100}%`);
        el.classList.add("cc-tilt--active");
      });
    },
    onLeave() {
      const el = this.$refs.tilt;
      if (!el) return;
      el.classList.remove("cc-tilt--active");
      el.style.setProperty("--ry", "0deg");
      el.style.setProperty("--rx", "0deg");
    },
  },
};
</script>

<style scoped>
.cc-scene {
  perspective: 1200px;
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  padding: 8px;
  animation: cc-enter 0.9s cubic-bezier(0.2, 0.9, 0.25, 1) both;
}

/* Tres camadas, cada uma com um transform so seu: flutuar, inclinar, virar. */
.cc-float {
  animation: cc-float 6s ease-in-out infinite;
}
.cc-float--busy {
  animation-duration: 1.6s;
}

.cc-tilt {
  --rx: 0deg;
  --ry: 0deg;
  --gx: 50%;
  --gy: 50%;
  transform-style: preserve-3d;
  transform: rotateX(var(--rx)) rotateY(var(--ry));
  transition: transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.cc-tilt--active {
  transition: transform 0.12s linear;
}

.cc-card {
  position: relative;
  width: 100%;
  aspect-ratio: 1.586;
  transform-style: preserve-3d;
  transition: transform 0.7s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  border-radius: 18px;
}

.cc-card:focus-visible {
  outline: 3px solid rgba(255, 85, 149, 0.55);
  outline-offset: 4px;
}

.cc-card.flipped {
  transform: rotateY(180deg);
}

.cc-face {
  position: absolute;
  inset: 0;
  border-radius: 18px;
  padding: 22px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #fff;
  box-shadow: 0 18px 40px -12px rgba(107, 18, 51, 0.55),
    inset 0 1px 1px rgba(255, 255, 255, 0.25);
}

.cc-card--busy .cc-face {
  animation: cc-pulse 1.2s ease-in-out infinite;
}

/* Paleta do aluno (rosa/magenta) */
.cc-front {
  background: linear-gradient(
    135deg,
    var(--dk-pink, #ff5595) 0%,
    var(--dk-pink, #e8276f) 45%,
    var(--dk-pink-deep, #6b1233) 100%
  );
}

.cc-back {
  background: linear-gradient(
    135deg,
    var(--dk-pink-deep, #8c1843) 0%,
    var(--dk-pink-deep, #6b1233) 60%,
    #47103a 100%
  );
  transform: rotateY(180deg);
}

/* Brilho diagonal que percorre o cartao */
.cc-shine {
  position: absolute;
  top: -60%;
  left: -30%;
  width: 60%;
  height: 220%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.35) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: rotate(20deg);
  animation: cc-sweep 5s ease-in-out infinite;
  pointer-events: none;
}
.cc-card--busy .cc-shine {
  animation-duration: 1.3s;
}

/* Reflexo que acompanha o ponteiro */
.cc-glare {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at var(--gx) var(--gy),
    rgba(255, 255, 255, 0.28),
    rgba(255, 255, 255, 0) 55%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}
.cc-tilt--active .cc-glare {
  opacity: 1;
}

/* Numero completo: passada rapida, uma vez */
.cc-burst {
  position: absolute;
  top: -60%;
  left: -40%;
  width: 40%;
  height: 220%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.55) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: rotate(20deg);
  animation: cc-burst 0.9s ease-out forwards;
  pointer-events: none;
}

.cc-front__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cc-chip {
  width: 46px;
  height: 34px;
  border-radius: 7px;
  background: linear-gradient(135deg, #f6d365 0%, #d9a441 100%);
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 3px;
  padding: 6px;
  box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.25);
}
.cc-chip span {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 2px;
}

.cc-brand {
  display: flex;
  align-items: center;
}
.cc-brand__text {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}
.cc-brand--back {
  margin-top: auto;
  justify-content: flex-end;
  opacity: 0.85;
}

.cc-number {
  margin-top: auto;
  font-family: "Work Sans", monospace;
  font-size: clamp(1.1rem, 5vw, 1.55rem);
  letter-spacing: 3px;
  font-weight: 600;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  white-space: pre;
}

/* Campo em edicao no formulario: a regiao correspondente acende no cartao */
.cc-slot {
  border-radius: 8px;
  transition: background-color 0.25s ease, box-shadow 0.25s ease;
}
.cc-slot--active {
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 0 0 5px rgba(255, 255, 255, 0.16);
}

/* Cada caractere novo entra com um pequeno salto */
.cc-char {
  display: inline-block;
  white-space: pre;
}
.cc-char--typed {
  animation: cc-pop 0.32s cubic-bezier(0.2, 1.4, 0.4, 1);
}

.cc-front__bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 16px;
}

.cc-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.cc-field--right {
  align-items: flex-end;
}
.cc-label {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.7;
}
.cc-value {
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}
.cc-holder {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Verso */
.cc-stripe {
  height: 42px;
  margin: 6px -22px 16px;
  background: rgba(0, 0, 0, 0.55);
}
.cc-signature {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  background: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.85),
    rgba(255, 255, 255, 0.85) 6px,
    rgba(255, 255, 255, 0.65) 6px,
    rgba(255, 255, 255, 0.65) 12px
  );
  border-radius: 6px;
  padding: 8px 12px;
  color: var(--dk-pink-deep, #6b1233);
}
.cc-signature__label {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}
.cc-cvv {
  font-family: "Work Sans", monospace;
  font-weight: 700;
  letter-spacing: 2px;
}
.cc-back__note {
  font-size: 0.62rem;
  opacity: 0.8;
  margin-top: 14px;
  line-height: 1.4;
}

@keyframes cc-enter {
  from {
    opacity: 0;
    transform: translateY(28px) rotateX(-20deg) scale(0.94);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes cc-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes cc-sweep {
  0% {
    left: -40%;
  }
  60%,
  100% {
    left: 130%;
  }
}

@keyframes cc-burst {
  from {
    left: -40%;
    opacity: 1;
  }
  to {
    left: 140%;
    opacity: 0;
  }
}

@keyframes cc-pop {
  0% {
    opacity: 0;
    transform: translateY(6px) scale(0.7);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes cc-pulse {
  0%,
  100% {
    box-shadow: 0 18px 40px -12px rgba(107, 18, 51, 0.55),
      inset 0 1px 1px rgba(255, 255, 255, 0.25);
  }
  50% {
    box-shadow: 0 18px 54px -8px rgba(255, 85, 149, 0.75),
      inset 0 1px 1px rgba(255, 255, 255, 0.25);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cc-scene,
  .cc-float,
  .cc-shine,
  .cc-burst,
  .cc-char--typed,
  .cc-card--busy .cc-face {
    animation: none;
  }
  .cc-tilt,
  .cc-card,
  .cc-glare {
    transition: none;
  }
  .cc-tilt {
    transform: none;
  }
}
</style>
